import type { AllocationKey, CostCategory } from './enums';

/** CO2KostAufG Anlage, step table [lowerInclusive kg/m²/yr, upperExclusive|null, landlordPercent]. G8. */
export const DEFAULT_CO2_STEPS: [number, number | null, number][] = [
  [0, 12, 0], [12, 17, 10], [17, 22, 20], [22, 27, 30], [27, 32, 40],
  [32, 37, 50], [37, 42, 60], [42, 47, 70], [47, 52, 80], [52, null, 95],
];

/**
 * CO2KostAufG changed on 23.07.2026 (new §§ 5a–5d, § 7 Abs. 5 on gas and oil heating under § 43 GModG, docs/19 row 33).
 * The engine still applies the step model above; the change is with the lawyer. Until then the apps show a hint for
 * periods reaching that day with gas or oil heating (or an unknown heating type). No calculation depends on it.
 */
export const CO2_LAW_CHANGED_ON = '2026-07-23';
export const co2LawReviewHint = (periodTo: string, heatingType?: string | null) =>
  periodTo >= CO2_LAW_CHANGED_ON && (!heatingType || heatingType === 'central_gas' || heatingType === 'central_oil');

/**
 * Default allocation keys per cost category (overridable per account/property/tenancy/cost item).
 * § 556a Abs. 1 BGB (docs/15 #45, ADR-0019): without an agreement in the lease the living area applies, except for costs
 * recorded by consumption. Persons or units only via the lease (tenancy key overrides) or a property setting.
 */
export const DEFAULT_KEYS: Record<CostCategory, AllocationKey> = {
  property_tax: 'AREA_DAYS',
  water: 'CONSUMPTION',
  sewage: 'CONSUMPTION',
  heating: 'METERING_RESULT',
  hot_water: 'METERING_RESULT',
  elevator: 'AREA_DAYS',
  street_cleaning: 'AREA_DAYS',
  waste: 'AREA_DAYS',
  building_cleaning: 'AREA_DAYS',
  pest_control: 'AREA_DAYS',
  garden: 'AREA_DAYS',
  common_electricity: 'AREA_DAYS',
  chimney: 'AREA_DAYS',
  insurance: 'AREA_DAYS',
  caretaker: 'AREA_DAYS',
  antenna_cable: 'AREA_DAYS',
  laundry: 'AREA_DAYS',
  other_operating: 'AREA_DAYS',
};

/** Keys that need an agreement in the lease (§ 556a Abs. 1 BGB): UI shows "nur wenn im Mietvertrag vereinbart". */
export const KEYS_NEED_LEASE_AGREEMENT: readonly AllocationKey[] = ['PERSON_DAYS', 'UNIT_DAYS'];

export const RULES = {
  /** § 556 Abs. 3 BGB: statement must reach tenant within 12 months after period end. */
  statementDeadlineMonths: 12,
  /** Tenant objection period after receipt (former tenants may read until then). */
  objectionPeriodMonths: 12,
  maxPeriodDays: 366,
  vacancyFictivePersonsDefault: 1,
  prepaymentSuggestionBufferPctDefault: 0,
  reminderDaysDefault: [60, 30, 7],
  /** Invite code lifetime. */
  inviteTtlDays: 14,
  magicLinkTtlMinutes: 30,
  uploadMaxBytes: 25 * 1024 * 1024,
  uploadUrlTtlSeconds: 15 * 60,
  readUrlTtlSeconds: 10 * 60,
  accountDeletionGraceDays: 14,
  /** Review threshold for AI extracted fields. */
  aiConfidenceThreshold: 0.9,
  /** Arrears banner threshold in monthly rents. */
  arrearsWarnMonths: 2,
  /** Cable TV is not apportionable from this date (TKMoG). */
  cableTvNotApportionableFrom: '2024-07-01',
  mainMeterDiffWarnPct: 20,
  /** Tenants are asked to read their meters this many days before the period ends (job readingRequests). */
  readingRequestDaysBefore: 7,
  /** Calibration reminder once the calibration date is this close (or passed). */
  calibrationReminderDays: 30,
} as const;
